import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Button } from './Button';

export interface ConfirmOptions {
  title: string;
  description?: string | undefined;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  variant?: 'default' | 'danger' | undefined;
  /**
   * When present, the dialog renders a required acknowledgment checkbox and the
   * confirm button stays disabled until it is ticked. Omitted by all existing
   * callers, who are unaffected.
   */
  acknowledge?: { label: string } | undefined;
  /**
   * Extra content under the description — e.g. a choice or a note. It keeps
   * its own state; read it after the dialog resolves.
   */
  body?: ReactNode | undefined;
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

interface PendingConfirm {
  opts: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [ack, setAck] = useState(false);
  const titleId = useId();
  const descId = useId();
  const ackId = useId();
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const ackCheckboxRef = useRef<HTMLInputElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const resolveWith = useCallback(
    (value: boolean) => {
      setPending((curr) => {
        curr?.resolve(value);
        return null;
      });
    },
    [],
  );

  const confirm = useCallback<ConfirmContextValue['confirm']>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        setPending({ opts, resolve });
      }),
    [],
  );

  useEffect(() => {
    if (!pending) return;
    // A fresh dialog always opens unticked.
    setAck(false);
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // When an acknowledgment is required the confirm button opens disabled and
    // therefore can't receive focus — focus the checkbox so the focus trap and
    // the alertdialog announcement still work. Otherwise focus confirm as before.
    const t = window.setTimeout(() => {
      if (pending.opts.acknowledge) ackCheckboxRef.current?.focus();
      else confirmBtnRef.current?.focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        resolveWith(false);
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [pending, resolveWith]);

  const value = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending
        ? createPortal(
            <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4">
              <div
                onClick={() => resolveWith(false)}
                aria-hidden="true"
                className="absolute inset-0 bg-ink-900/50 transition-opacity duration-200"
              />
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={pending.opts.description ? descId : undefined}
                className={clsx(
                  'relative w-full max-w-md rounded-lg bg-[color:var(--color-bg-elevated)] shadow-xl',
                  'transition-transform duration-200 ease-out',
                )}
              >
                <div className="px-5 py-4 border-b border-[color:var(--color-border)]">
                  <h2
                    id={titleId}
                    className="font-display text-lg font-semibold text-[color:var(--color-text)]"
                  >
                    {pending.opts.title}
                  </h2>
                </div>
                {pending.opts.description ? (
                  <div className="px-5 py-4">
                    <p
                      id={descId}
                      className="text-sm text-[color:var(--color-text-muted)]"
                    >
                      {pending.opts.description}
                    </p>
                  </div>
                ) : null}
                {pending.opts.body ? <div className="px-5 pb-4">{pending.opts.body}</div> : null}
                {pending.opts.acknowledge ? (
                  <div className="border-t border-[color:var(--color-border)] px-5 py-4">
                    <label
                      htmlFor={ackId}
                      className="flex cursor-pointer items-start gap-3 text-sm text-[color:var(--color-text)]"
                    >
                      <input
                        ref={ackCheckboxRef}
                        id={ackId}
                        type="checkbox"
                        checked={ack}
                        onChange={(e) => setAck(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-[color:var(--color-border)] text-gold-500 accent-gold-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                      />
                      <span className="leading-snug">{pending.opts.acknowledge.label}</span>
                    </label>
                  </div>
                ) : null}
                <div className="flex justify-end gap-2 border-t border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-5 py-3">
                  <Button variant="ghost" onClick={() => resolveWith(false)}>
                    {pending.opts.cancelLabel ?? 'Cancel'}
                  </Button>
                  <Button
                    ref={confirmBtnRef}
                    variant={pending.opts.variant === 'danger' ? 'danger' : 'primary'}
                    disabled={Boolean(pending.opts.acknowledge) && !ack}
                    onClick={() => resolveWith(true)}
                  >
                    {pending.opts.confirmLabel ?? 'Confirm'}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};
